import React, { useEffect, useRef, useState } from "react";
import "./QuantumResearch.css";

export default function QuantumSuperpositionField({ width = 640, height = 400 }) {
  const canvasRef = useRef(null);
  const [numSources, setNumSources] = useState(6);
  const [running, setRunning] = useState(true);
  const [freq, setFreq] = useState(0.04);
  const [colorMode, setColorMode] = useState("rgb");
  const tRef = useRef(0);
  const [sources, setSources] = useState([]);

  // initialize random sources
  useEffect(() => {
    const s = Array.from({ length: numSources }).map(() => ({
      x: Math.random(),
      y: Math.random(),
      phase: Math.random() * Math.PI * 2,
      colorPhase: Math.random() * Math.PI * 2
    }));
    setSources(s);
  }, [numSources]);

  useEffect(() => {
    let raf;
    const ctx = canvasRef.current.getContext("2d");

    function draw() {
      const w = canvasRef.current.width;
      const h = canvasRef.current.height;
      const img = ctx.createImageData(w, h);
      const data = img.data;
      tRef.current += 0.02;
      const t = tRef.current;

      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const px = x / w;
          const py = y / h;
          let rSum = 0, gSum = 0, bSum = 0;

          for (let s of sources) {
            const dx = px - s.x;
            const dy = py - s.y;
            const r = Math.sqrt(dx * dx + dy * dy) + 0.001;
            const k = 40 * freq;  // spatial frequency
            const wave = Math.sin(2 * Math.PI * k * r - 2 * Math.PI * freq * t + s.phase);

            if (colorMode === "rgb") {
              rSum += Math.sin(wave + s.colorPhase);
              gSum += Math.sin(wave + s.colorPhase + 2);
              bSum += Math.sin(wave + s.colorPhase + 4);
            } else {
              rSum += wave;
              gSum += wave;
              bSum += wave;
            }
          }

          // Normalize & amplify
          const n = sources.length;
          const R = Math.tanh(rSum / n * 2);
          const G = Math.tanh(gSum / n * 2);
          const B = Math.tanh(bSum / n * 2);

          const i = (y * w + x) * 4;
          data[i] = Math.floor(128 + 127 * R);
          data[i + 1] = Math.floor(128 + 127 * G);
          data[i + 2] = Math.floor(128 + 127 * B);
          data[i + 3] = 255;
        }
      }

      ctx.putImageData(img, 0, 0);
      if (running) raf = requestAnimationFrame(draw);
    }

    if (running) raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [sources, freq, running, colorMode]);

  const randomize = () => {
    setSources((s) => s.map((so) => ({
      ...so,
      phase: Math.random() * Math.PI * 2,
      colorPhase: Math.random() * Math.PI * 2
    })));
  };

  return (
    <div className="superposition-root">
      <h2>🌈 Quantum Superposition Field</h2>
      <p className="qr-subtitle">
        Watch colorful interference fringes emerge from multi-qubit phase overlap.
      </p>

      <div style={{ display: "flex", gap: 14, alignItems: "center", justifyContent: "center", marginBottom: 10 }}>
        <label>
          Sources:
          <input
            type="range"
            min="2"
            max="20"
            value={numSources}
            onChange={(e) => setNumSources(Number(e.target.value))}
          />
          {numSources}
        </label>
        <label>
          Freq:
          <input
            type="range"
            min="0.01"
            max="0.1"
            step="0.005"
            value={freq}
            onChange={(e) => setFreq(Number(e.target.value))}
          />
        </label>
        <button className="qr-btn" onClick={randomize}>Randomize Phases</button>
        <button className="qr-btn" onClick={() => setRunning(r => !r)}>
          {running ? "Pause" : "Play"}
        </button>
        <button
          className="qr-btn"
          onClick={() => setColorMode(m => (m === "rgb" ? "mono" : "rgb"))}
        >
          {colorMode === "rgb" ? "Monochrome" : "RGB Mode"}
        </button>
      </div>

      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          border: "2px solid var(--neon-cyan)",
          borderRadius: "12px",
          display: "block",
          margin: "0 auto",
          background: "black",
          boxShadow: "0 0 20px rgba(0,255,255,0.15)"
        }}
      />

      <p style={{ textAlign: "center", marginTop: 10, color: "var(--neon-green)" }}>
        Interference = overlapping phase waves | Colors represent phase channels
      </p>
    </div>
  );
}
